import { forwardRef, PropsWithChildren, useEffect, useImperativeHandle, useState } from 'react';

import {
	Close as CloseIcon,
	DescriptionOutlined as DescriptionOutlinedIcon,
	FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import {
	Card,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useFilePicker } from '~hooks';
import { formatBytes } from '~utils';

/**
 * TODO: this is still to focused on image uploading. Rebuild to fit
 * general files
 */

interface FileInput {
	file: File;
	uri: string;
}

export type DragAndDropFilesUploaderProps = {
	title?: string;
	readonly?: boolean;
	maxFileSizeInBytes?: number;
	accept?: string;
	multiple?: boolean; // allow selecting multiple files
	/**
	 *
	 * @param file The image as file
	 * @param uri
	 * @returns
	 */
	files?: FileInput[];
	onChange?: (files: FileInput[]) => void;
	onError?: (errors: Error[]) => void;
	clearable?: boolean;
	editable?: boolean;
} & PropsWithChildren;

export interface DragAndDropFilesUploaderRef {
	openFilePicker: () => void;
}

/**
 * Work in progress!!!
 *
 */
const DragAndDropFilesUploader = forwardRef<
	DragAndDropFilesUploaderRef,
	DragAndDropFilesUploaderProps
>(
	(
		{
			files,
			children,
			maxFileSizeInBytes = 10240000,
			clearable = true,
			editable = true,
			multiple = false,
			readonly = false,
			title,
			...props
		},
		ref
	) => {
		const { t } = useTranslation();

		const filePicker = useFilePicker({
			multiple: multiple,
			accept: props.accept,
		});
		const [hovered, setHovered] = useState(false);

		useEffect(() => {
			props.onChange?.(filePicker.files);
		}, [filePicker.files]);

		useImperativeHandle(ref, () => ({
			openFilePicker: filePicker.openFilePicker,
		}));

		const handleClick = (e) => {
			if (files?.length > 0) {
				console.debug(files);
				return;
			}

			e.preventDefault();
			filePicker.openFilePicker();
		};

		const handleRemoveFileClick = (e: React.ChangeEvent<HTMLDivElement>, index: number) => {
			e.preventDefault();
			e.stopPropagation();

			const newFiles = filePicker.files?.filter((_, i) => i != index);
			filePicker.setFiles(newFiles);
		};

		return (
			<div
				onClick={handleClick}
				onDrop={filePicker.dropFiles}
				onDragOver={(event) => event.preventDefault()}
				onMouseOver={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
			>
				{files != null && files.length > 0 ?
					<Card sx={{ p: 1 }}>
						<List>
							{files.map((el, i, arr) => (
								<ListItem
									divider={i < arr.length - 1}
									secondaryAction={
										<IconButton edge='end' onClick={(e) => handleRemoveFileClick(e, i)}>
											<CloseIcon />
										</IconButton>
									}
								>
									<ListItemIcon>
										<DescriptionOutlinedIcon />
									</ListItemIcon>
									<ListItemText
										primary={el.file?.name}
										secondary={el.file?.size ? formatBytes(el.file?.size, 1, 'decimal') : null}
									/>
								</ListItem>
							))}
						</List>
					</Card>
				:	<DragAndDropUploaderBox
						title={title}
						maxFileSizeInBytes={maxFileSizeInBytes}
						error={filePicker.errors[0]}
					/>
				}
			</div>
		);
	}
);

interface DragAndDropFilesUploaderBox {
	title?: string;
	maxFileSizeInBytes: number;
	error?: Error | null;
}

const DragAndDropUploaderBox = ({
	maxFileSizeInBytes = 1024000,
	title = 'Upload PDF',
	...props
}: DragAndDropFilesUploaderBox) => {
	const { t } = useTranslation();
	const theme = useTheme();

	return (
		<Stack
			direction='column'
			spacing={2}
			sx={{
				p: 3,
				alignItems: 'center',
				border: `1px dashed ${theme.palette.grey[200]}`,
				'&:hover': {
					borderColor: theme.palette.grey[400],
					cursor: 'pointer',
				},
			}}
		>
			<FileUploadIcon fontSize='large' />
			<Typography variant='h5'>{title}</Typography>
			<Typography variant='body2' color={props.error != null ? 'error' : undefined}>
				{`${t('ui.maxFileSize')}: ${formatBytes(maxFileSizeInBytes, 0, 'decimal')}`}
			</Typography>
			{props.error && (
				<Typography variant='body2' color={props.error != null ? 'error' : undefined}>
					{props.error.message}
				</Typography>
			)}
		</Stack>
	);
};

export default DragAndDropFilesUploader;
